import { forwardRef, useId, useMemo } from 'react';
import Highcharts from '../highcharts';
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';

export type AccessibleChartData = {
  columns: string[];
  rows: Array<Array<string | number | null | undefined>>;
};

type AccessibleChartProps = {
  // Several existing charts use custom point properties for report exports and
  // tooltip formatting, so accept their extended Highcharts option objects.
  options: any;
  title: string;
  description: string;
  className?: string;
  data?: AccessibleChartData;
};

function textValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return 'No data';
  }

  if (typeof value === 'number' && value > 100000000000) {
    return new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric' }).format(new Date(value));
  }

  return String(value);
}

function getAxisCategories(options: any): Array<string | number> {
  const xAxis = Array.isArray(options.xAxis) ? options.xAxis[0] : options.xAxis;
  return ((xAxis as any)?.categories || []) as Array<string | number>;
}

function getPointValue(point: any): unknown {
  if (point?.accessibility?.description) {
    return point.accessibility.description;
  }
  if (point?.customCategory) {
    return point.customCategory;
  }
  if (point?.x2 !== undefined) {
    return `${textValue(point.x)} to ${textValue(point.x2)}`;
  }
  if (point?.y !== undefined) {
    return point.y;
  }
  if (Array.isArray(point)) {
    return point[1];
  }
  return point;
}

function getPointCategory(point: any, index: number, categories: Array<string | number>): unknown {
  if (categories[index] !== undefined) {
    return categories[index];
  }
  if (point?.category !== undefined) {
    return point.category;
  }
  if (point?.customDate !== undefined) {
    return point.customDate;
  }
  if (point?.name !== undefined) {
    return point.name;
  }
  if (point?.x !== undefined) {
    return point.x;
  }
  if (Array.isArray(point)) {
    return point[0];
  }
  return index + 1;
}

export function chartDataFromOptions(options: any): AccessibleChartData {
  const series = (options.series || []) as any[];
  const categories = getAxisCategories(options);
  const maximumPoints = Math.max(categories.length, ...series.map((item) => item.data?.length || 0), 0);
  const columns = ['Category', ...series.map((item, index) => item.name || (series.length === 1 ? 'Value' : `Series ${index + 1}`))];
  const rows = Array.from({ length: maximumPoints }, (_, index) => {
    const firstPoint = series.find((item) => item.data?.[index] !== undefined)?.data[index];
    return [
      textValue(getPointCategory(firstPoint, index, categories)),
      ...series.map((item) => textValue(getPointValue(item.data?.[index])))
    ];
  });

  return { columns, rows };
}

const AccessibleChart = forwardRef<HighchartsReactRefObject, AccessibleChartProps>(function AccessibleChart(
  { options, title, description, className, data },
  ref
) {
  const captionId = useId();
  const tableData = useMemo(() => data || chartDataFromOptions(options), [data, options]);
  const accessibleOptions = useMemo<Highcharts.Options>(() => ({
    ...options,
    accessibility: {
      ...options.accessibility,
      enabled: true,
      description,
      keyboardNavigation: {
        ...options.accessibility?.keyboardNavigation,
        enabled: true
      },
      screenReaderSection: {
        ...options.accessibility?.screenReaderSection,
        beforeChartFormat: `<div>{chartTitle}</div><div>{typeDescription}</div><div>{chartSubtitle}</div><div>{chartLongdesc}</div>`
      }
    }
  }), [description, options]);

  return (
    <figure className={`accessible-chart${className ? ` ${className}` : ''}`} aria-labelledby={captionId}>
      <figcaption id={captionId} className="visually-hidden">{title}. {description}</figcaption>
      <HighchartsReact ref={ref} highcharts={Highcharts} options={accessibleOptions} />
      <details className="accessible-chart-data">
        <summary>View {title} data</summary>
        <div className="accessible-chart-data-scroll" tabIndex={0} role="region" aria-label={`${title} data table`}>
          <table>
            <caption>{title} data</caption>
            <thead>
              <tr>{tableData.columns.map((column) => <th scope="col" key={column}>{column}</th>)}</tr>
            </thead>
            <tbody>
              {tableData.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => cellIndex === 0
                    ? <th scope="row" key={cellIndex}>{textValue(cell)}</th>
                    : <td key={cellIndex}>{textValue(cell)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  );
});

export default AccessibleChart;
